import { ChangeDetectionStrategy, Component, inject } from '@angular/core';
import { RouterLink, RouterOutlet } from '@angular/router';

import { BrandingService } from './core/branding.service';
import { LocaleService } from './core/locale.service';

/**
 * The portal shell (prototype 01 exactly): skip link, the rekruto top bar - 34px mark +
 * 19px wordmark left, the da/en toggle right, 1080px wrap - the routed page (the tenant
 * identity lives in the hero eyebrow, the top bar stays rekruto chrome), and the rekruto
 * footer. Calm public-service surface. The shared chrome (skip link, top bar, mark,
 * wordmark, language switch, footer) lives in shared/rekruto-ui.css; this shell carries
 * only its own deltas: the space-between bar rhythm and the two-link footer row.
 */
@Component({
  selector: 'app-root',
  imports: [RouterOutlet, RouterLink],
  changeDetection: ChangeDetectionStrategy.OnPush,
  styles: `
    .rk-portal {
      min-height: 100vh;
      display: flex;
      flex-direction: column;
      background: var(--rk-bg);
      color: var(--rk-text);
      font-family: var(--rk-font-sans);
    }
    .rk-logo {
      display: flex;
      align-items: center;
      gap: 10px;
    }
    .rk-topbar-inner {
      justify-content: space-between;
      gap: 14px;
    }
    .rk-foot-inner {
      display: flex;
      justify-content: space-between;
      flex-wrap: wrap;
      gap: 8px;
    }
    .rk-foot a {
      color: var(--rk-link);
      font-weight: 500;
      min-height: 44px;
      display: inline-flex;
      align-items: center;
    }
    @media (max-width: 480px) {
      .rk-topbar-inner,
      .rk-foot-inner {
        padding-left: 16px;
        padding-right: 16px;
      }
    }
  `,
  template: `
    <div class="rk-portal rk-root" [class.rk-tenant-brand]="branding.branded()">
      <a class="rk-skip" href="#indhold">{{ t().skipToContent }}</a>

      <header class="rk-topbar">
        <div class="rk-topbar-inner">
          <span class="rk-logo">
            <span class="rk-mark" aria-hidden="true"></span>
            <span class="rk-wordmark">rekruto</span>
          </span>
          <div class="rk-lang" role="group" [attr.aria-label]="t().chooseLanguage">
            <button
              type="button"
              [attr.aria-pressed]="loc.locale() === 'da'"
              (click)="loc.set('da')"
            >
              DA
            </button>
            <button
              type="button"
              [attr.aria-pressed]="loc.locale() === 'en'"
              (click)="loc.set('en')"
            >
              EN
            </button>
          </div>
        </div>
      </header>

      <main id="indhold" class="w-full flex-1">
        <router-outlet />
      </main>

      <footer class="rk-foot">
        <div class="rk-foot-inner">
          <span>{{ t().poweredBy }}</span>
          <a routerLink="/hvad-ai-ser">{{ t().whatAiSeesLink }}</a>
        </div>
      </footer>
    </div>
  `,
})
export class App {
  private readonly brandingService = inject(BrandingService);
  private readonly localeService = inject(LocaleService);

  protected readonly t = () => this.localeService.strings();
  protected readonly loc = this.localeService;
  protected readonly branding = this.brandingService;
}
